/* eslint-disable import/no-unresolved */
import { useState, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import {
  PayPalScriptProvider,
  PayPalButtons,
  PayPalCardFieldsProvider,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField,
  usePayPalCardFields,
  // eslint-disable-next-line import/extensions
} from '@paypal/react-paypal-js';
import { WIX_INSTANCE_PARAMS } from '../../actions/component_actions';
import { WidgetLoader } from '../../loader/widget-loader';
import styles from './paypal-payment-form.scss';
import { StepButtonWrapper } from './checkout-stepper';
import { trackPaymentAttempt, trackPaymentResult } from '../../utils/journey-tracking';

export function PayPalPaymentForm({
  children,
  ticketOverrides,
  settings,
  cart,
  form,
  eventId,
  onPaymentSubmit,
  promo_code,
  access_code,
  checkoutValues,
  session,
  onResizeComponent,
  event,
  onBack,
  selectedAddons,
}) {
  const [clientToken, setClientToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const total = checkoutValues.cartTotal;

  useEffect(() => {
    if (+total === 0) return undefined;
    (async () => {
      const { data } = await axios.post(
        `${process.env.EVENTVIEWER_SERVER}/api/v1/paypal/checkout/generate-client-id${WIX_INSTANCE_PARAMS}`,
        {},
        {
          withCredentials: true,
        }
      );
      setClientToken(data.client_token);
      setTimeout(() => {
        // give slight delay so resie can kick in. A hack!
        onResizeComponent();
        setLoading(false);
      }, 800);
    })();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrder = async () => {
    // Prepare tracking data
    const eventData = {
      event_category: 'Ticket',
      event_label: event?.get('name'),
      currency: event?.get('currency'),
      payment_method: 'paypal',
      cart_id: session?.idempotency_key,
    };

    // Google Analytics tracking
    if (window.gtag) {
      window.gtag('event', 'begin_payment', eventData);
    }

    // Facebook Pixel tracking
    if (window.fbq) {
      window.fbq('trackCustom', 'InitiatePayment', {
        content_name: event?.get('name'),
        content_category: 'Ticket',
        payment_method: 'paypal',
        currency: event?.get('currency'),
        value: checkoutValues?.total || 0,
      });
    }

    // Journey tracking for payment attempt
    if (event) {
      trackPaymentAttempt(event, { cart_id: session?.idempotency_key });
    }

    // Convert selectedAddons to addon_selections format for API
    const addon_selections =
      selectedAddons && selectedAddons.toJS
        ? selectedAddons.toJS().map((selection) => ({
            event_addon_id: selection.event_addon_id,
            addon_event_id: selection.addon_event_id,
            ticket_id: selection.ticket_id,
            occurrence_id: selection.occurrence_id,
          }))
        : [];

    const { data } = await axios.post(`${process.env.EVENTVIEWER_SERVER}/api/v1/paypal/checkout/orders${WIX_INSTANCE_PARAMS}`, {
      cart,
      event_id: eventId,
      email: form.email,
      first_name: form.first_name,
      last_name: form.last_name,
      promo_code,
      access_code,
      ticket_overrides: ticketOverrides,
      addon_selections,
    });
    return data.id;
  };

  if (+total === 0) {
    return (
      <div className={styles.button}>
        {React.cloneElement(children, {
          onClick: () => onPaymentSubmit({ idempotency_key: session.idempotency_key }),
        })}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14, minHeight: 200 }}>
      <WidgetLoader loading={loading} isRelative>
        <PayPalPayment
          handleOrder={handleOrder}
          clientToken={clientToken}
          onPaymentSubmit={onPaymentSubmit}
          buttonComponent={children}
          settings={settings}
          form={form}
          event={event}
          session={session}
          checkoutValues={checkoutValues}
          onBack={onBack}
        />
      </WidgetLoader>
    </div>
  );
}

function PayPalPayment({ handleOrder, onPaymentSubmit, buttonComponent, settings, form, event, session, checkoutValues, onBack }) {
  const paypalEnableCheckout = session.payment.paypal.paypal_enable_checkout;
  const enableCheckout = paypalEnableCheckout == null ? true : paypalEnableCheckout;
  const enableCreditCard = session.payment.paypal.paypal_enable_credit_card_fields;
  const enablePayLater = session.payment.paypal.paypal_enable_pay_later;

  const handleApprove = async (data) => {
    // Journey tracking
    if (event) {
      trackPaymentResult(event, true, {
        order_id: data.orderID,
        payment_method: 'paypal',
        cart_id: session?.idempotency_key,
      });
    }

    setTimeout(async () => {
      await onPaymentSubmit({
        bank_token: data.orderID,
        payment_type: 'paypal',
      });
    }, 0);
  };

  const handleError = (error) => {
    const errorMessage = error?.message || 'Payment failed';
    if (event) {
      trackPaymentResult(event, false, {
        error_message: errorMessage,
        payment_method: 'paypal',
      });
    }

    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'payment_error', {
        event_category: 'Ticket',
        event_label: event?.get('name'),
        error_message: errorMessage,
        payment_method: 'paypal',
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'PaymentError', {
        content_name: event?.get('name'),
        content_category: 'Ticket',
        payment_method: 'paypal',
        error_message: errorMessage,
      });
    }
  };

  const handleCancel = (data) => {
    console.log('PayPal payment cancelled:', data);
  };

  const initialOptions = {
    'client-id': process.env.PAYPAL_CLIENT_ID,
    currency: window.currency || 'USD',
    intent: 'capture',
    components: 'buttons,card-fields',
    disableFunding: enablePayLater ? '' : 'paylater',
  };

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PaymentButtons
        handleOrder={handleOrder}
        handleApprove={handleApprove}
        handleError={handleError}
        handleCancel={handleCancel}
        enableCheckout={enableCheckout}
        enablePayLater={enablePayLater}
        enableCreditCard={enableCreditCard}
        buttonComponent={buttonComponent}
        settings={settings}
        form={form}
        event={event}
        session={session}
        checkoutValues={checkoutValues}
        onPaymentSubmit={onPaymentSubmit}
        onBack={onBack}
      />
    </PayPalScriptProvider>
  );
}

function PaymentButtons({
  handleOrder,
  handleApprove,
  handleError,
  handleCancel,
  enableCheckout,
  enableCreditCard,
  buttonComponent,
  settings,
  form,
  event,
  onPaymentSubmit,
  onBack,
}) {
  return (
    <div className={styles.paypalButtons}>
      {/* PayPal Checkout Button - Conditional */}
      {enableCheckout && (
        <PayPalButtons
          createOrder={handleOrder}
          onApprove={handleApprove}
          onCancel={handleCancel}
          onError={handleError}
          style={{
            layout: 'vertical',
            shape: 'rect',
            color: 'gold',
            label: 'paypal',
          }}
        />
      )}

      {/* Custom Card Fields - Conditional */}
      {enableCreditCard && (
        <CustomCardFields
          handleOrder={handleOrder}
          checkoutValues={handleOrder}
          handleApprove={handleApprove}
          buttonComponent={buttonComponent}
          settings={settings}
          form={form}
          event={event}
          onPaymentSubmit={onPaymentSubmit}
          onBack={onBack}
        />
      )}
    </div>
  );
}

/**
 * Submit Payment Component
 * Handles card field submission using PayPal SDK hooks
 */
const SubmitPayment = ({ buttonComponent, form, onPaymentSubmit, event }) => {
  const [processing, setProcessing] = useState(false);

  const { cardFieldsForm } = usePayPalCardFields();
  const { first_name, last_name } = form;
  const handlePayment = async (ev) => {
    // const { first_name, last_name, email } = form;
    ev && ev.preventDefault();

    setProcessing(true);
    try {
      // if (typeof cardFields.submit === "function") return;
      await cardFieldsForm.submit({
        // The full name as shown in the card and billing address
        cardholderName: `${first_name} ${last_name}`,
      });

      setProcessing(false);
    } catch (data) {
      const { details: [{ description } = {}] = [], message, response } = data;
      const result = description || response?.data || message;

      // Track payment error
      if (event) {
        trackPaymentResult(event, false, {
          error_message: result,
          payment_method: 'paypal',
        });
      }

      // Google Analytics tracking for payment error
      if (window.gtag) {
        window.gtag('event', 'payment_error', {
          event_category: 'Ticket',
          event_label: event?.get('name'),
          error_message: result,
          payment_method: 'paypal',
        });
      }

      // Facebook Pixel tracking for payment error
      if (window.fbq) {
        window.fbq('trackCustom', 'PaymentError', {
          content_name: event?.get('name'),
          content_category: 'Ticket',
          payment_method: 'paypal',
          error_message: result,
        });
      }

      await onPaymentSubmit({
        error: result,
      });
    }
  };

  return React.cloneElement(buttonComponent, {
    disabled: processing,
    className: processing ? 'button-saving' : '',
    onClick: handlePayment,
  });
};

/**
 * Custom Credit Card Fields Component
 * This component renders PayPal's hosted card fields directly in your form
 */
function CustomCardFields({ checkoutValues, handleOrder, handleApprove, buttonComponent, settings, form, event, onPaymentSubmit, onBack }) {
  const isMobile = window.isMobileType;

  const cardFieldsStyle = {
    input: {
      'font-size': '14px',
      'font-family': 'Arial, sans-serif',
      padding: '12px',
      color: '#333',
    },
    '.invalid': {
      color: '#dc3545',
    },
  };

  return (
    <div className={styles.paypalCreditCard}>
      <PayPalCardFieldsProvider createOrder={handleOrder} onApprove={handleApprove} style={cardFieldsStyle} amount={checkoutValues.cartTotal}>
        <Row className={isMobile ? styles.row : ''}>
          <Col xs={12}>
            <label className={`checkout-form-field ${styles.label}`} htmlFor="card-number">
              <span className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                </svg>
              </span>{' '}
              {settings.get('cardNumberText') || 'Card Number'}
            </label>
            <PayPalNumberField
              id="card-number"
              className="card-field"
              style={{
                input: {
                  border: '1px solid #606060',
                  'box-shadow': '2px 2px 10px 2px rgba(0,0,0,0.1)',
                  height: '50px',
                  'border-radius': '8px',
                  background: 'white',
                  color: 'black',
                  padding: '12px',
                },
              }}
            />
          </Col>
        </Row>
        <Row className={isMobile ? styles.row : ''} style={{ marginTop: 10 }}>
          <Col xs={isMobile ? '12' : '6'}>
            <label className={`checkout-form-field ${styles.label}`} htmlFor="card-expiry">
              <span className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </svg>
              </span>
              {settings.get('cardExpiryText') || 'Expiration Date'}
            </label>
            <PayPalExpiryField
              id="card-expiry"
              className="card-field"
              style={{
                input: {
                  border: '1px solid #606060',
                  'box-shadow': '2px 2px 10px 2px rgba(0,0,0,0.1)',
                  height: '50px',
                  'border-radius': '8px',
                  background: 'white',
                  color: 'black',
                  padding: '12px',
                },
              }}
            />
          </Col>
          <Col xs={isMobile ? '12' : '6'}>
            <label className={`checkout-form-field ${styles.label}`} htmlFor="card-cvv">
              <span className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                </svg>
              </span>
              {settings.get('cardSecurityCodeText') || 'Security Code'}
            </label>
            <PayPalCVVField
              id="card-cvv"
              className="card-field"
              style={{
                input: {
                  border: '1px solid #606060',
                  'box-shadow': '2px 2px 10px 2px rgba(0,0,0,0.1)',
                  height: '50px',
                  'border-radius': '8px',
                  background: 'white',
                  color: 'black',
                  padding: '12px',
                },
              }}
            />
          </Col>
        </Row>

        {/* Button Container with Back and Submit */}
        <div className={styles.paymentButtonContainer}>
          <StepButtonWrapper>
            <Button variant="outline-dark" onClick={onBack} className="checkout-prev-button checkout-back-button">
              {settings.get('checkoutBackButtonText')}
            </Button>
          </StepButtonWrapper>
          <SubmitPayment buttonComponent={buttonComponent} settings={settings} form={form} onPaymentSubmit={onPaymentSubmit} event={event} />
        </div>
      </PayPalCardFieldsProvider>
    </div>
  );
}
